'use strict';

/**
 * Accessibility layer for the homepage content carousels driven by tiny-slider: the
 * Page Designer product slider ("Popular Styles") and blog slider ("Curated
 * Inspiration"). Unlike the header promo banner these never rotate on their own; the
 * shopper moves them with Previous / Next, a drag, or by tabbing into a slide.
 *
 * WCAG 1.3.1 (Info and Relationships) - list structure
 *   The track is a `role="list"` of `role="listitem"` slides carrying
 *   aria-posinset / aria-setsize (authored in the template), labelled by the section
 *   heading. tiny-slider puts `aria-hidden` on every slide that is off screen, which
 *   shrinks the list a screen reader reports to whatever happens to be visible
 *   ("list, 2 items"). Real slides are therefore kept in the accessibility tree at all
 *   times; only tiny-slider's loop clones - duplicates of real slides - are hidden.
 *   Clones stay clickable (no `inert`): when the loop wraps, a clone is what the
 *   shopper sees and presses.
 *
 * WCAG 2.1.1 / 2.4.3 - reaching off-screen slides
 *   Because every real slide stays reachable, focus landing on one that is off screen
 *   (Tab, or a screen reader's browse cursor) brings it into view instead of leaving
 *   the focus ring clipped by the slider's overflow.
 *
 * WCAG 1.3.1 / 4.1.3 (Status Messages) - Previous / Next
 *   tiny-slider renders its controls as a focusable, role-less <div> holding buttons
 *   taken out of the tab order. The wrapper becomes a labelled group and the buttons
 *   become ordinary tab stops. Focus stays on the button that was pressed, and a
 *   polite status message says which items are now showing ("Showing items 3 to 4 of
 *   4"). tiny-slider's own hard-coded English live region is silenced so the move is
 *   not announced twice.
 */

var SLIDE = '.tns-item';
var FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';
var CLONE_CLASS = 'tns-slide-cloned';
var STATUS_CLASS = 'content-carousel-status';

var DEFAULT_LABELS = {
    controls: '{0} carousel controls',
    controlsDefault: 'Carousel controls',
    previous: 'Previous',
    next: 'Next',
    statusRange: 'Showing items {0} to {1} of {2}',
    statusSingle: 'Showing item {0} of {1}',
    statusList: 'Showing items {0} of {1}'
};

// Share of a slide's width that must be on screen for it to count as "showing". The
// fractional peek of the next slide sits at exactly this share, so it has to be exceeded,
// by more than EDGE_TOLERANCE, rather than merely reached.
var VISIBLE_RATIO = 0.5;
// Sub-pixel tolerance for layout rounding when testing whether a slide is fully in view.
var EDGE_TOLERANCE = 1;

var idSeed = 0;

/**
 * Collapse whitespace and trim.
 * @param {string} text - raw text
 * @returns {string} normalized text
 */
function normalize(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
}

/**
 * Fill `{0}`-style placeholders. Resource.msg hands the pattern over unformatted so the
 * values can be supplied on the client.
 * @param {string} pattern - text with numbered placeholders
 * @param {Array} values - replacement values
 * @returns {string} formatted text
 */
function format(pattern, values) {
    return pattern.replace(/\{(\d+)\}/g, function (match, i) {
        return values[i] !== undefined ? values[i] : match;
    });
}

/**
 * Make sure the element has an id that aria attributes can point at.
 * @param {jQuery} $element - the element
 * @returns {string} the element's id
 */
function ensureId($element) {
    var id = $element.attr('id');
    if (!id) {
        idSeed += 1;
        id = 'content-carousel-' + idSeed;
        $element.attr('id', id);
    }
    return id;
}

/**
 * The labels are authored in the template so they stay translatable; fall back to
 * English rather than ship a control with no accessible name.
 * @param {jQuery} $track - the tiny-slider container
 * @returns {Object} label lookup
 */
function readLabels($track) {
    return {
        controls: normalize($track.data('controlsLabel')) || DEFAULT_LABELS.controls,
        controlsDefault: normalize($track.data('controlsLabelDefault')) || DEFAULT_LABELS.controlsDefault,
        previous: normalize($track.data('prevText')) || DEFAULT_LABELS.previous,
        next: normalize($track.data('nextText')) || DEFAULT_LABELS.next,
        statusRange: normalize($track.data('statusRange')) || DEFAULT_LABELS.statusRange,
        statusSingle: normalize($track.data('statusSingle')) || DEFAULT_LABELS.statusSingle,
        statusList: normalize($track.data('statusList')) || DEFAULT_LABELS.statusList
    };
}

/**
 * The slides that are real content, in order - everything but tiny-slider's clones.
 * @param {jQuery} $track - the tiny-slider container
 * @returns {jQuery} the real slides
 */
function getRealSlides($track) {
    return $track.children(SLIDE).not('.' + CLONE_CLASS);
}

/**
 * Keep every real slide in the accessibility tree and every clone out of it.
 *
 * tiny-slider re-applies `aria-hidden` / `tabindex="-1"` to off-screen slides on each
 * move and, for autoWidth sliders, on resize without emitting an event - so this runs
 * from a MutationObserver rather than a slider event. Only writes when something is
 * out of place, which keeps the observer from feeding itself.
 * @param {jQuery} $track - the tiny-slider container
 */
function exposeSlides($track) {
    $track.children(SLIDE).each(function () {
        var $slide = $(this);

        if ($slide.hasClass(CLONE_CLASS)) {
            if ($slide.attr('aria-hidden') !== 'true') {
                $slide.attr('aria-hidden', 'true');
            }
            // A clone's links would be a second tab stop for the same product; Tab
            // reaches the real slide instead, which focus-follow brings into view.
            $slide.find(FOCUSABLE).each(function () {
                if (this.getAttribute('tabindex') !== '-1') {
                    this.setAttribute('tabindex', '-1');
                }
            });
            return;
        }

        if (this.hasAttribute('aria-hidden')) {
            $slide.removeAttr('aria-hidden');
        }
        if (this.hasAttribute('tabindex')) {
            $slide.removeAttr('tabindex');
        }
    });
}

/**
 * @param {jQuery} $track - the tiny-slider container
 */
function bindSlideExposure($track) {
    exposeSlides($track);

    if (!window.MutationObserver) {
        return;
    }

    new window.MutationObserver(function () {
        exposeSlides($track);
    }).observe($track[0], {
        attributes: true,
        attributeFilter: ['aria-hidden', 'tabindex'],
        subtree: true
    });
}

/**
 * Whether the slide sits entirely inside the slider viewport.
 * @param {Element} slide - the slide
 * @param {Element} viewport - the tiny-slider overflow wrapper
 * @returns {boolean} true when nothing of the slide is clipped
 */
function isFullyVisible(slide, viewport) {
    var slideRect = slide.getBoundingClientRect();
    var viewRect = viewport.getBoundingClientRect();

    return slideRect.left >= viewRect.left - EDGE_TOLERANCE
        && slideRect.right <= viewRect.right + EDGE_TOLERANCE;
}

/**
 * 1-based list positions of the slides that are showing, in on-screen order. A slide
 * counts once more than half of it is on screen, so the fractional peek of the next
 * slide is left out. A clone counts as the slide it copies - its `aria-posinset` came
 * with the copy - so a wrapped loop reads as e.g. [5, 1].
 * @param {jQuery} $track - the tiny-slider container
 * @param {Element} viewport - the tiny-slider overflow wrapper
 * @returns {Array<number>} visible positions, in on-screen order, without repeats
 */
function getVisiblePositions($track, viewport) {
    var viewRect = viewport.getBoundingClientRect();
    var $real = getRealSlides($track);
    var positions = [];

    $track.children(SLIDE).each(function () {
        var rect = this.getBoundingClientRect();
        var overlap = Math.min(rect.right, viewRect.right) - Math.max(rect.left, viewRect.left);
        if (rect.width <= 0 || overlap - EDGE_TOLERANCE <= rect.width * VISIBLE_RATIO) {
            return;
        }

        var position = parseInt(this.getAttribute('aria-posinset'), 10);
        if (isNaN(position)) {
            // Without the template's position a clone cannot be placed; a real slide can.
            position = $real.index(this) + 1;
        }
        if (position > 0 && positions.indexOf(position) === -1) {
            positions.push(position);
        }
    });

    return positions;
}

/**
 * The status message for the showing positions: a single item, a run ("3 to 4"), or -
 * where the loop wraps back to the start - the items listed in on-screen order.
 * @param {Array<number>} positions - visible positions, in on-screen order
 * @param {number} total - number of real slides
 * @param {Object} labels - label lookup
 * @returns {string} the message
 */
function describePositions(positions, total, labels) {
    if (positions.length === 1) {
        return format(labels.statusSingle, [positions[0], total]);
    }

    for (var i = 1; i < positions.length; i += 1) {
        if (positions[i] !== positions[i - 1] + 1) {
            return format(labels.statusList, [positions.join(', '), total]);
        }
    }

    return format(labels.statusRange, [positions[0], positions[positions.length - 1], total]);
}

/**
 * The slider clips with `overflow: hidden`, which a browser will still scroll to show a
 * focused element - shifting the whole track out of step with tiny-slider's transform.
 * Positioning belongs to tiny-slider alone, so any such scroll is undone.
 * @param {jQuery} $track - the tiny-slider container
 * @param {jQuery} $region - the component root
 */
function lockWrapperScroll($track, $region) {
    $track.parentsUntil($region.parent()).each(function () {
        var wrapper = this;
        $(wrapper).on('scroll', function () {
            if (wrapper.scrollLeft !== 0) {
                wrapper.scrollLeft = 0;
            }
        });
    });
}

/**
 * Silence tiny-slider's injected "slide x of y" live region: its text is hard-coded
 * English and counts clones, and the carousel announces itself through its own status
 * message. Hidden too, so browse mode does not read it out as stray content.
 * @param {jQuery} $outer - the tiny-slider outer wrapper
 */
function quietSliderLiveRegion($outer) {
    $outer.find('.tns-liveregion').attr({
        'aria-live': 'off',
        'aria-hidden': 'true'
    });
}

/**
 * Turn tiny-slider's controls into a labelled group of ordinary buttons. tiny-slider
 * makes the wrapper <div> the tab stop and takes both buttons out of the tab order,
 * which leaves a keyboard user on an unnamed, role-less element.
 * @param {Object} info - tiny-slider info
 * @param {Object} labels - label lookup
 * @param {string} headingText - the carousel's heading
 */
function decorateControls(info, labels, headingText) {
    var $group = $(info.controlsContainer);
    if (!$group.length) {
        return;
    }

    $group.removeAttr('tabindex').attr({
        role: 'group',
        'aria-label': headingText ? format(labels.controls, [headingText]) : labels.controlsDefault
    });

    $(info.prevButton).removeAttr('tabindex').attr('aria-label', labels.previous);
    $(info.nextButton).removeAttr('tabindex').attr('aria-label', labels.next);
}

/**
 * tiny-slider emits `transitionEnd` for every transitionend that bubbles up to the
 * track, including hover and focus transitions inside a slide (the product tile
 * animates on hover). Only the track's own transform - or `left`, where transforms are
 * unsupported - is a slide move. A missing event means tiny-slider ran the move with
 * no transition and called the handler itself, which is a move too.
 * @param {Object} endInfo - tiny-slider info passed to the transitionEnd handler
 * @param {Element} track - the tiny-slider container
 * @returns {boolean} whether the event marks the end of a slide move
 */
function isSlideMove(endInfo, track) {
    var event = endInfo && endInfo.event;
    if (!event || !event.target) {
        return true;
    }
    return event.target === track && /transform|left/i.test(event.propertyName || '');
}

/**
 * Run once tiny-slider has built its tools. For autoWidth sliders it defers that until
 * the slide images have loaded, so `tns()` can return before the controls exist; the
 * live region is inserted in the same pass and marks the moment.
 * @param {jQuery} $outer - the tiny-slider outer wrapper
 * @param {Function} callback - run when the tools are in the DOM
 */
function whenToolsReady($outer, callback) {
    if ($outer.find('.tns-liveregion').length || !window.MutationObserver) {
        callback();
        return;
    }

    var observer = new window.MutationObserver(function () {
        if ($outer.find('.tns-liveregion').length) {
            observer.disconnect();
            callback();
        }
    });
    observer.observe($outer[0], { childList: true, subtree: true });
}

/**
 * Apply the content carousel accessibility layer to an initialised tiny-slider.
 * @param {Object} slider - the tiny-slider instance returned by tns()
 * @param {Object} options - settings
 * @param {jQuery} options.$region - the component root that holds heading and carousel
 * @param {jQuery} [options.$heading] - the heading that names the carousel
 */
function init(slider, options) {
    if (!slider || !slider.getInfo) {
        return;
    }

    var info = slider.getInfo();
    var $track = $(info.container);
    var $outer = $track.closest('.tns-outer');
    var $viewport = $track.closest('.tns-ovh');
    if (!$track.length || !$outer.length || !$viewport.length) {
        return;
    }

    var $region = options.$region && options.$region.length ? options.$region : $outer;
    var $heading = options.$heading && options.$heading.length ? options.$heading.first() : $();
    var headingText = normalize($heading.text());
    var labels = readLabels($track);
    var followingFocus = false;

    if ($heading.length) {
        $track.attr('aria-labelledby', ensureId($heading));
    }

    var $status = $('<div>', {
        'class': 'sr-only ' + STATUS_CLASS,
        role: 'status',
        'aria-live': 'polite',
        'aria-atomic': 'true'
    });
    $outer.after($status);

    bindSlideExposure($track);
    lockWrapperScroll($track, $region);

    whenToolsReady($outer, function () {
        quietSliderLiveRegion($outer);
        decorateControls(slider.getInfo(), labels, headingText);
    });

    // A press focuses its target as the mousedown's default action, in the same task as
    // the mousedown itself, so a flag cleared on the next tick covers exactly that focus.
    var pointerFocus = false;
    $track.on('mousedown pointerdown', function () {
        pointerFocus = true;
        setTimeout(function () {
            pointerFocus = false;
        }, 0);
    });

    // Bring an off-screen slide into view when focus lands in it. The move is not
    // announced: focus arriving on the slide already tells the shopper where they are.
    // Pointer focus is left alone: sliding a partly visible slide away mid-click would
    // make the click miss the link the shopper pressed.
    $track.on('focusin', function (event) {
        if (pointerFocus) {
            return;
        }

        var $slide = $(event.target).closest(SLIDE);
        if (!$slide.length || $slide.hasClass(CLONE_CLASS) || isFullyVisible($slide[0], $viewport[0])) {
            return;
        }

        var before = slider.getInfo().index;
        followingFocus = true;
        slider.goTo(getRealSlides($track).index($slide));

        // A slide pinned at the end of the track can already be as far in as it goes;
        // with no move there is no transitionEnd to clear the flag.
        if (slider.getInfo().index === before) {
            followingFocus = false;
        }
    });

    // Say what the move revealed. Emptied first so pressing Next twice onto the same
    // range still speaks the second time.
    slider.events.on('transitionEnd', function (endInfo) {
        if (!isSlideMove(endInfo, $track[0])) {
            return;
        }

        if (followingFocus) {
            followingFocus = false;
            return;
        }

        var positions = getVisiblePositions($track, $viewport[0]);
        if (!positions.length) {
            return;
        }

        var message = describePositions(positions, getRealSlides($track).length, labels);

        $status.text('');
        setTimeout(function () {
            $status.text(message);
        }, 100);
    });
}

module.exports = {
    init: init
};
